import React from "react";
import { Entity } from "@common/types/entity";
import { ConsolidationRelationshipResponse } from "@common/schemas/domains/accounting/consolidation.schema";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import {
    getOwnershipEntityDisplayName,
    RELATIONSHIP_PARTY_LABELS,
    RELATIONSHIP_TYPE_LABELS,
} from "../ownershipGraph.helpers";
import styles from "./Pane.module.scss";

interface RelationshipPropertiesProps {
    relationship: ConsolidationRelationshipResponse;
    entitiesByUuid: Map<string, Entity>;
}

interface PropertyRow {
    label: string;
    value: string | null;
}

/**
 * The property rows for a single relationship — shared by the single
 * relationship pane and each section of the bundle pane so both render
 * identically.
 */
export const RelationshipProperties: React.FC<RelationshipPropertiesProps> = ({
    relationship,
    entitiesByUuid,
}) => {
    const partyLabels = RELATIONSHIP_PARTY_LABELS[relationship.type];
    const sourceEntity = entitiesByUuid.get(relationship.sourceEntityUuid);
    const targetEntity = entitiesByUuid.get(relationship.targetEntityUuid);
    const isOwnership =
        relationship.type === ConsolidationEntityRelationshipType.ownership;

    const rows: PropertyRow[] = [
        {
            label: "Relationship type",
            value: RELATIONSHIP_TYPE_LABELS[relationship.type],
        },
        {
            label: partyLabels.source,
            value: sourceEntity
                ? getOwnershipEntityDisplayName(sourceEntity)
                : "Unknown entity",
        },
        {
            label: partyLabels.target,
            value: targetEntity
                ? getOwnershipEntityDisplayName(targetEntity)
                : "Unknown entity",
        },
        {
            label: "Ownership",
            value:
                isOwnership && relationship.ownershipPercentage !== null
                    ? `${relationship.ownershipPercentage}%`
                    : null,
        },
        {
            label: "Ownership type",
            value: relationship.metadata?.ownershipType ?? null,
        },
        { label: "Payment", value: relationship.metadata?.payment ?? null },
        {
            label: "Target balance",
            value: relationship.metadata?.targetBalance ?? null,
        },
        { label: "Frequency", value: relationship.metadata?.frequency ?? null },
        { label: "Start date", value: relationship.startDate },
        { label: "End date", value: relationship.endDate },
        { label: "Role", value: relationship.metadata?.roleLabel ?? null },
        { label: "Notes", value: relationship.metadata?.notes ?? null },
    ].filter(({ value }) => value !== null);

    return (
        <>
            {rows.map(({ label, value }) => (
                <div key={label} className={styles.detailRow}>
                    <span className={styles.detailLabel}>{label}</span>
                    <span className={styles.detailValue}>{value}</span>
                </div>
            ))}
        </>
    );
};
